import {
    Box,
    Button,
    Chip,
    CircularProgress,
    debounce,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined'
import { useState, useEffect, useMemo, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { IListItemValue } from '../../interfaces/Interfaces'
import { useFetchOrganizations } from '../../hooks/useFetchOrganizations'
import { diffOrgSelection } from './helperFunctions'
import PaperComponent from '../Common/PaperComponent'
import CheckboxInput from '../Common/CheckboxInput'
import TruncatedText from '../Common/TruncatedText'

interface OrgPickerModalProps {
    open: boolean
    title: string
    value: IListItemValue[]
    savedValue: IListItemValue[] // original server-loaded value — never changes within a session
    excludeOrgs?: string[]
    onApply: (selected: IListItemValue[]) => void
    onCancel: () => void
}

const OrgPickerModal = ({
    open,
    title,
    value,
    savedValue,
    excludeOrgs,
    onApply,
    onCancel,
}: OrgPickerModalProps): ReactElement => {
    const { t } = useTranslation()
    const [selected, setSelected] = useState<IListItemValue[]>(value)

    const { orgsList, isFetching, hasMore, onSearch, fetchNextPage } =
        useFetchOrganizations({ excludeOrgs })

    // Debounced to match SearchInAPIAndMultiSelectInput's search behavior elsewhere in the
    // app — without this, every keystroke fired an immediate API call.
    const debouncedSearch = useMemo(
        () => debounce((text: string) => onSearch('', text), 300),
        [onSearch],
    )

    // Cancel any pending debounced call when the modal unmounts (e.g. user types then
    // immediately hits Cancel/Apply before the 300ms elapses), so it never fires against a
    // gone component instance.
    useEffect(() => {
        return () => debouncedSearch.clear()
    }, [debouncedSearch])

    useEffect(() => {
        if (open) {
            setSelected(value)
        }
    }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

    // Diff always computed against the original saved state, not the last applied state —
    // this drives the Summary panel and the footer counts.
    const { addedIds, addedOrgs, removedOrgs } = useMemo(
        () => diffOrgSelection(savedValue, selected),
        [selected, savedValue],
    )

    // Separate diff against the currently-APPLIED value (not the original baseline), used only
    // to gate the Apply button. Without this, undoing a previously-applied selection (so
    // `selected` goes back to matching `savedValue`) left the Apply button disabled — the diff
    // vs. the original baseline read as "no change", even though `selected` still differed from
    // the field's current applied `value` and Apply needed to be clickable to save the revert.
    const { addedOrgs: pendingAdded, removedOrgs: pendingRemoved } = useMemo(
        () => diffOrgSelection(value, selected),
        [selected, value],
    )

    const isChecked = (org: IListItemValue) =>
        selected.some((s) => s.id === org.id)

    const toggleOrg = (org: IListItemValue) => {
        setSelected((prev) =>
            prev.some((s) => s.id === org.id)
                ? prev.filter((s) => s.id !== org.id)
                : [...prev, org],
        )
    }

    const removeSelected = (id: string) =>
        setSelected((prev) => prev.filter((s) => s.id !== id))

    const restoreOrg = (org: IListItemValue) =>
        setSelected((prev) => [...prev, org])

    const handleLeftScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
        if (
            scrollHeight - scrollTop - clientHeight < 100 &&
            hasMore &&
            !isFetching
        ) {
            fetchNextPage()
        }
    }

    return (
        <Dialog
            open={open}
            onClose={onCancel}
            maxWidth="md"
            fullWidth
            PaperComponent={PaperComponent}
            PaperProps={{
                sx: {
                    borderRadius: '8px',
                    height: '80vh',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            <DialogTitle
                id="draggable-dialog-title"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pb: 1,
                    flexShrink: 0,
                    cursor: 'move',
                }}
            >
                <Typography fontWeight={700} fontSize={16}>
                    {title}
                </Typography>
                <IconButton
                    size="small"
                    onClick={onCancel}
                    aria-label={t('close')}
                    id="org-picker-close-button"
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent
                sx={{
                    display: 'flex',
                    gap: 0,
                    overflow: 'hidden',
                    px: 3,
                    py: 1,
                    flex: 1,
                }}
            >
                {/* Left panel — Available */}
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        border: '1px solid #DDE2E5',
                        borderRadius: '4px 0 0 4px',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            px: 1.5,
                            py: 1,
                            borderBottom: '1px solid #DDE2E5',
                            bgcolor: '#FAFBFC',
                            flexShrink: 0,
                        }}
                    >
                        <Typography
                            fontSize={11}
                            fontWeight={700}
                            color="text.secondary"
                            sx={{
                                textTransform: 'uppercase',
                                letterSpacing: '0.6px',
                            }}
                        >
                            {t('available')}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            px: 1.5,
                            py: 1,
                            borderBottom: '1px solid #DDE2E5',
                            flexShrink: 0,
                        }}
                    >
                        <TextField
                            size="small"
                            fullWidth
                            id="org-picker-search"
                            placeholder={t('search')}
                            onChange={(e) => debouncedSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon
                                            sx={{
                                                fontSize: 16,
                                                color: 'text.secondary',
                                            }}
                                        />
                                    </InputAdornment>
                                ),
                                sx: { fontSize: 13 },
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '4px',
                                },
                            }}
                        />
                    </Box>
                    <Box
                        id="org-picker-available-list"
                        sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}
                        onScroll={handleLeftScroll}
                    >
                        <List dense disablePadding>
                            {orgsList.map((org) => {
                                const checked = isChecked(org)
                                return (
                                    <ListItem
                                        key={org.id}
                                        disablePadding
                                        sx={{
                                            px: 1.5,
                                            py: 0.25,
                                            minWidth: 0,
                                            '&:hover': {
                                                bgcolor: '#F3F8F8',
                                            },
                                            ...(checked && {
                                                bgcolor: '#E2ECE9',
                                                '&:hover': {
                                                    bgcolor: '#d4e6e1',
                                                },
                                            }),
                                        }}
                                    >
                                        <CheckboxInput
                                            id={org.id}
                                            label={org.name}
                                            value={checked}
                                            onChange={() => toggleOrg(org)}
                                            truncateLabel
                                        />
                                    </ListItem>
                                )
                            })}
                            {isFetching && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        py: 1.5,
                                    }}
                                >
                                    <CircularProgress size={16} />
                                </Box>
                            )}
                        </List>
                    </Box>
                </Box>

                {/* Divider */}
                <Divider orientation="vertical" flexItem />

                {/* Right panel — Summary of changes */}
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        border: '1px solid #DDE2E5',
                        borderLeft: 'none',
                        borderRadius: '0 4px 4px 0',
                        overflow: 'hidden',
                    }}
                >
                    {/* Header */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            px: 1.5,
                            py: 1,
                            borderBottom: '1px solid #DDE2E5',
                            bgcolor: '#FAFBFC',
                            flexShrink: 0,
                        }}
                    >
                        <Typography
                            fontSize={11}
                            fontWeight={700}
                            color="text.secondary"
                            sx={{
                                textTransform: 'uppercase',
                                letterSpacing: '0.6px',
                            }}
                        >
                            {t('summary')}
                        </Typography>
                        {(addedOrgs.length > 0 || removedOrgs.length > 0) && (
                            <Box sx={{ display: 'flex', gap: 0.75 }}>
                                {addedOrgs.length > 0 && (
                                    <Typography
                                        fontSize={11}
                                        sx={{
                                            color: '#1e7e34',
                                            fontWeight: 600,
                                        }}
                                    >
                                        +{addedOrgs.length}
                                    </Typography>
                                )}
                                {removedOrgs.length > 0 && (
                                    <Typography
                                        fontSize={11}
                                        sx={{
                                            color: '#c62828',
                                            fontWeight: 600,
                                        }}
                                    >
                                        -{removedOrgs.length}
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Box>

                    {/* Body */}
                    <Box sx={{ flex: 1, overflowY: 'auto' }}>
                        {addedOrgs.length === 0 && removedOrgs.length === 0 ? (
                            <Box sx={{ py: 6, textAlign: 'center' }}>
                                <Typography
                                    fontSize={13}
                                    color="text.secondary"
                                >
                                    {t('noChangesToDisplay')}
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                {/* Added section */}
                                {addedOrgs.length > 0 && (
                                    <>
                                        <Box
                                            sx={{
                                                px: 1.5,
                                                py: 0.75,
                                                bgcolor: '#f0faf2',
                                                borderBottom:
                                                    '1px solid #e6f4ea',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                            }}
                                        >
                                            <Typography
                                                fontSize={11}
                                                fontWeight={700}
                                                sx={{
                                                    color: '#1e7e34',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px',
                                                }}
                                            >
                                                {t('added')}
                                            </Typography>
                                            <Chip
                                                label={addedOrgs.length}
                                                size="small"
                                                sx={{
                                                    height: 16,
                                                    fontSize: 10,
                                                    bgcolor: '#1e7e34',
                                                    color: '#fff',
                                                    '& .MuiChip-label': {
                                                        px: 0.75,
                                                    },
                                                }}
                                            />
                                        </Box>
                                        <List dense disablePadding>
                                            {addedOrgs.map((org) => (
                                                <ListItem
                                                    key={org.id}
                                                    disablePadding
                                                    secondaryAction={
                                                        <Tooltip
                                                            title={t('undo')}
                                                            placement="top"
                                                            arrow
                                                        >
                                                            <IconButton
                                                                edge="end"
                                                                size="small"
                                                                onClick={() =>
                                                                    removeSelected(
                                                                        org.id,
                                                                    )
                                                                }
                                                                sx={{
                                                                    color: '#1e7e34',
                                                                    opacity: 0.6,
                                                                    '&:hover': {
                                                                        opacity: 1,
                                                                    },
                                                                }}
                                                            >
                                                                <CloseIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    }
                                                    sx={{
                                                        borderBottom:
                                                            '1px solid #f0f0f0',
                                                    }}
                                                >
                                                    <ListItemText
                                                        primary={
                                                            <TruncatedText
                                                                text={org.name}
                                                                sx={{
                                                                    fontSize: 13,
                                                                    color: '#1e7e34',
                                                                    fontWeight: 500,
                                                                }}
                                                            />
                                                        }
                                                        sx={{
                                                            pl: 1.5,
                                                            py: 0.75,
                                                            pr: 6,
                                                        }}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </>
                                )}

                                {/* Removed section */}
                                {removedOrgs.length > 0 && (
                                    <>
                                        <Box
                                            sx={{
                                                px: 1.5,
                                                py: 0.75,
                                                bgcolor: '#fff8f8',
                                                borderBottom:
                                                    '1px solid #fdecea',
                                                borderTop:
                                                    addedOrgs.length > 0
                                                        ? '1px solid #DDE2E5'
                                                        : 'none',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                            }}
                                        >
                                            <Typography
                                                fontSize={11}
                                                fontWeight={700}
                                                sx={{
                                                    color: '#c62828',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px',
                                                }}
                                            >
                                                {t('removed')}
                                            </Typography>
                                            <Chip
                                                label={removedOrgs.length}
                                                size="small"
                                                sx={{
                                                    height: 16,
                                                    fontSize: 10,
                                                    bgcolor: '#c62828',
                                                    color: '#fff',
                                                    '& .MuiChip-label': {
                                                        px: 0.75,
                                                    },
                                                }}
                                            />
                                        </Box>
                                        <List dense disablePadding>
                                            {removedOrgs.map((org) => (
                                                <ListItem
                                                    key={org.id}
                                                    disablePadding
                                                    secondaryAction={
                                                        <Tooltip
                                                            title={t('restore')}
                                                            placement="top"
                                                            arrow
                                                        >
                                                            <IconButton
                                                                edge="end"
                                                                size="small"
                                                                onClick={() =>
                                                                    restoreOrg(
                                                                        org,
                                                                    )
                                                                }
                                                                sx={{
                                                                    color: '#c62828',
                                                                    opacity: 0.6,
                                                                    '&:hover': {
                                                                        opacity: 1,
                                                                    },
                                                                }}
                                                            >
                                                                <UndoOutlinedIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    }
                                                    sx={{
                                                        borderBottom:
                                                            '1px solid #f0f0f0',
                                                    }}
                                                >
                                                    <ListItemText
                                                        primary={
                                                            <TruncatedText
                                                                text={org.name}
                                                                sx={{
                                                                    fontSize: 13,
                                                                    color: '#c62828',
                                                                    textDecoration:
                                                                        'line-through',
                                                                    opacity: 0.8,
                                                                }}
                                                            />
                                                        }
                                                        sx={{
                                                            pl: 1.5,
                                                            py: 0.75,
                                                            pr: 6,
                                                        }}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </>
                                )}
                            </>
                        )}
                    </Box>
                </Box>
            </DialogContent>

            {/* Footer */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 3,
                    py: 1.5,
                    borderTop: '1px solid #DDE2E5',
                    flexShrink: 0,
                }}
            >
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    <Typography fontSize={12} color="text.secondary">
                        {t('selectedCount', { count: selected.length })}
                    </Typography>
                    {addedIds.size > 0 && (
                        <Typography fontSize={12} sx={{ color: '#1e7e34' }}>
                            {t('addedCount', { count: addedIds.size })}
                        </Typography>
                    )}
                    {removedOrgs.length > 0 && (
                        <Typography fontSize={12} sx={{ color: '#c62828' }}>
                            {t('removedCount', { count: removedOrgs.length })}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button
                        variant="outlined"
                        onClick={onCancel}
                        id="org-picker-cancel-button"
                        sx={{
                            fontSize: 12,
                            textTransform: 'none',
                            borderRadius: '4px',
                            px: 2.5,
                            borderColor: '#DDE2E5',
                            color: 'text.primary',
                        }}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => onApply(selected)}
                        id="org-picker-apply-button"
                        disabled={
                            pendingAdded.length === 0 &&
                            pendingRemoved.length === 0
                        }
                        sx={{
                            fontSize: 12,
                            textTransform: 'none',
                            borderRadius: '4px',
                            px: 2.5,
                            bgcolor: '#00797a',
                            '&:hover': { bgcolor: '#005f60' },
                        }}
                    >
                        {t('applySelections')}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    )
}

export default OrgPickerModal
